import { useParams, Link } from "react-router-dom";
import StickyHeader from "@/components/StickyHeader";
import Breadcrumbs from "@/components/Breadcrumbs";
import CTAFooter from "@/components/CTAFooter";
import { brands } from "@/data/brandContent";
import { ChevronLeft, Award, CheckCircle, Quote } from "lucide-react";

const BrandDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const brand = brands.find((b) => b.slug === slug);

  if (!brand) {
    return (
      <div className="min-h-screen bg-background">
        <StickyHeader />
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-3xl font-bold mb-4">Brand Not Found</h1>
          <Link to="/brands" className="text-primary hover:underline">
            ← Back to Brands
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <StickyHeader />
      <Breadcrumbs items={[{ label: "Brands", href: "/brands" }, { label: brand.name }]} />

      {/* Hero Banner */}
      <section className="relative bg-accent overflow-hidden">
        <div className="container mx-auto px-4 py-12 md:py-20">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <Link
                to="/brands"
                className="inline-flex items-center gap-1 text-accent-foreground/70 hover:text-accent-foreground mb-6 text-sm"
              >
                <ChevronLeft className="w-4 h-4" />
                See all brands
              </Link>
              <h1 className="text-4xl md:text-5xl font-bold font-montserrat text-accent-foreground mb-4">
                {brand.name}
              </h1>
              <p className="text-primary font-semibold text-lg mb-4">
                Specialist Dealer
              </p>
              <ul className="space-y-2 text-accent-foreground/80">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-primary" />
                  Free No-Obligation Onsite Quotation
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-primary" />
                  Same Day Installations
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-primary" />
                  Servicing the Southern Highlands
                </li>
              </ul>
              <div className="flex gap-4 mt-8">
                <Link
                  to="/brands"
                  className="px-6 py-3 border border-accent-foreground/30 text-accent-foreground rounded-lg hover:bg-accent-foreground/10 transition-colors text-sm font-semibold"
                >
                  See Other Brands
                </Link>
                <a
                  href="tel:0480841485"
                  className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-semibold"
                >
                  Contact Us Now
                </a>
              </div>
            </div>
            <div className="flex justify-center">
              <img
                src={brand.bannerImage}
                alt={`${brand.name} air conditioning range — authorised dealer products`}
                className="max-h-80 object-contain"
                width={600}
                height={320}
                loading="eager"
              />
            </div>
          </div>
        </div>

        {/* Savings badge */}
        <div className="absolute top-6 right-6 bg-primary text-primary-foreground rounded-full w-28 h-28 flex flex-col items-center justify-center text-center shadow-lg">
          <span className="text-xs font-medium">Save Up to</span>
          <span className="text-3xl font-bold">37%</span>
        </div>
      </section>

      {/* Authorised Dealer Badge */}
      <div className="bg-primary text-primary-foreground py-3 text-center">
        <p className="font-semibold tracking-wide">✓ Authorised Dealer</p>
      </div>

      {/* About Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-3 gap-12 items-start">
            <div className="md:col-span-2">
              <h2 className="text-3xl font-bold font-montserrat mb-6 text-foreground">
                About {brand.name}
              </h2>
              {brand.description.map((para, i) => (
                <p key={i} className="text-muted-foreground leading-relaxed mb-4">
                  {para}
                </p>
              ))}
              <Link
                to="/specials"
                className="inline-block mt-4 text-primary font-semibold hover:underline"
              >
                View Promotions on {brand.name} Air Conditioners →
              </Link>
            </div>
            <div className="flex flex-col items-center gap-6">
              <div className="bg-white rounded-xl shadow-md p-8 flex items-center justify-center w-full">
                <img
                  src={brand.logo}
                  alt={`${brand.name} official brand logo`}
                  className="max-h-20 object-contain"
                  width={160}
                  height={80}
                  loading="lazy"
                />
              </div>
              <img
                src={brand.dealerImage}
                alt={`${brand.name} authorised dealer certification — ACG Southern Highlands`}
                className="rounded-xl shadow-md w-full object-cover"
                width={400}
                height={300}
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      {brand.timeline && brand.timeline.length > 0 && (
        <section className="py-16 px-4 bg-muted">
          <div className="container mx-auto">
            <h2 className="text-3xl font-bold font-montserrat mb-10 text-center text-foreground">
              Our Story with {brand.name}
            </h2>
            <div className="relative max-w-3xl mx-auto">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-primary/30 transform md:-translate-x-1/2" />
              {brand.timeline.map((item, i) => (
                <div
                  key={i}
                  className={`relative flex items-start gap-6 mb-8 ${
                    i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                  }`}
                >
                  <div className="absolute left-4 md:left-1/2 w-3 h-3 bg-primary rounded-full transform -translate-x-1/2 mt-2" />
                  <div className={`ml-10 md:ml-0 md:w-1/2 ${i % 2 === 0 ? "md:pr-12 md:text-right" : "md:pl-12"}`}>
                    <span className="text-primary font-bold text-lg">{item.year}</span>
                    <p className="text-muted-foreground mt-1">{item.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Awards */}
      {brand.awards && brand.awards.length > 0 && (
        <section className="py-16 px-4 bg-muted">
          <div className="container mx-auto">
            <h2 className="text-3xl font-bold font-montserrat mb-10 text-center text-foreground">
              Awards & Recognition
            </h2>
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {brand.awards.map((award, i) => (
                <div key={i} className="bg-card rounded-xl shadow-md p-8 border border-border">
                  <div className="flex items-center gap-3 mb-4">
                    <Award className="w-8 h-8 text-primary" />
                    <div>
                      <h3 className="text-lg font-bold text-foreground">{award.name}</h3>
                      <span className="text-sm text-primary font-semibold">{award.years}</span>
                    </div>
                  </div>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {award.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Additional Sections */}
      {brand.sections.length > 0 && (
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-4xl">
            {brand.sections.map((section, i) => (
              <div key={i} className="mb-12">
                <h2 className="text-2xl font-bold font-montserrat mb-4 text-foreground">
                  {section.title}
                </h2>
                <p className="text-muted-foreground leading-relaxed">{section.content}</p>
              </div>
            ))}
          </div>
        </section>
      )}


      {/* Long Intro */}
      {brand.longIntro && brand.longIntro.length > 0 && (
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl font-bold font-montserrat mb-6 text-foreground">
              {brand.name} for Southern Highlands Homes
            </h2>
            {brand.longIntro.map((para, i) => (
              <p key={i} className="text-muted-foreground leading-relaxed mb-4">
                {para}
              </p>
            ))}
          </div>
        </section>
      )}

      {/* Cold Climate Notes */}
      {brand.coldClimateNotes && (
        <section className="py-16 px-4 bg-accent">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold font-montserrat mb-4 text-accent-foreground">
              Low-Ambient Heating &amp; Defrost Performance
            </h2>
            <p className="text-accent-foreground/85 leading-relaxed">{brand.coldClimateNotes}</p>
          </div>
        </section>
      )}

      {/* Range Breakdown */}
      {brand.rangeBreakdown && brand.rangeBreakdown.length > 0 && (
        <section className="py-16 px-4 bg-muted">
          <div className="container mx-auto">
            <h2 className="text-3xl font-bold font-montserrat mb-10 text-center text-foreground">
              {brand.name} Product Range
            </h2>
            <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
              {brand.rangeBreakdown.map((item, i) => (
                <div key={i} className="bg-card rounded-xl shadow-md p-6 border border-border">
                  <h3 className="text-lg font-bold text-foreground mb-2">{item.name}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Technology Highlights */}
      {brand.technologyHighlights && brand.technologyHighlights.length > 0 && (
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-5xl">
            <h2 className="text-3xl font-bold font-montserrat mb-10 text-center text-foreground">
              Technology Highlights
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {brand.technologyHighlights.map((item, i) => (
                <div key={i} className="flex gap-4">
                  <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-foreground mb-1">{item.title}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
            {brand.whoItSuits && (
              <div className="mt-12 bg-muted rounded-xl p-8 border border-border">
                <h3 className="text-xl font-bold text-foreground mb-3">Who {brand.name} Suits</h3>
                <p className="text-muted-foreground leading-relaxed">{brand.whoItSuits}</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Comparison Table */}
      {brand.comparisonRows && brand.comparisonRows.length > 0 && (
        <section className="py-16 px-4 bg-muted">
          <div className="container mx-auto max-w-3xl">
            <h2 className="text-3xl font-bold font-montserrat mb-8 text-center text-foreground">
              {brand.name} At a Glance
            </h2>
            <div className="bg-card rounded-xl shadow-md border border-border overflow-hidden">
              <table className="w-full text-sm">
                <tbody>
                  {brand.comparisonRows.map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? "bg-card" : "bg-muted/50"}>
                      <td className="py-3 px-6 font-semibold text-foreground w-1/3 align-top">{row.feature}</td>
                      <td className="py-3 px-6 text-muted-foreground align-top">{row.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {/* Testimonial */}
      {brand.testimonial && (
        <section className="py-16 px-4 bg-accent">
          <div className="container mx-auto max-w-3xl text-center">
            <Quote className="w-12 h-12 text-primary mx-auto mb-6" />
            <blockquote className="text-lg text-accent-foreground/90 italic leading-relaxed mb-6">
              "{brand.testimonial.quote}"
            </blockquote>
            <p className="font-bold text-accent-foreground">{brand.testimonial.author}</p>
            <p className="text-sm text-accent-foreground/70">{brand.testimonial.title}</p>
          </div>
        </section>
      )}

      {/* System Types */}
      <section className="py-16 px-4 bg-muted">
        <div className="container mx-auto">
          <h2 className="text-3xl font-bold font-montserrat mb-10 text-center text-foreground">
            {brand.name} System Types
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {(brand.acTypeLinks && brand.acTypeLinks.length > 0
              ? brand.acTypeLinks
              : [
                  { slug: "split-system", label: "Split System" },
                  { slug: "ducted-system", label: "Ducted System" },
                ]
            ).map((link, i) => (
              <Link
                key={i}
                to={`/ac-types/${link.slug}`}
                className="bg-card rounded-xl shadow-md p-8 border border-border hover:shadow-lg transition-shadow group"
              >
                <h3 className="text-xl font-bold text-foreground mb-2 group-hover:text-primary transition-colors">
                  {link.label}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Explore how {brand.name} {link.label.toLowerCase()} air conditioning performs across the Southern Highlands, including sizing, zoning and cold-climate heating considerations.
                </p>
                <span className="inline-block mt-4 text-primary font-semibold text-sm group-hover:underline">
                  Explore →
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      {brand.faqItems && brand.faqItems.length > 0 && (
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-3xl">
            <h2 className="text-3xl font-bold font-montserrat mb-10 text-center text-foreground">
              {brand.name} Frequently Asked Questions
            </h2>
            <div className="space-y-6">
              {brand.faqItems.map((item, i) => (
                <div key={i} className="bg-card rounded-xl shadow-md p-6 border border-border">
                  <h3 className="font-bold text-foreground mb-2">{item.question}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{item.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related Links */}
      <section className="py-12 px-4 bg-accent">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-2xl font-bold font-montserrat mb-4 text-accent-foreground">
            Ready to Talk {brand.name} Installation?
          </h2>
          <p className="text-accent-foreground/80 mb-6 leading-relaxed">
            ACG Southern Highlands supplies, installs and services {brand.name} systems across Mittagong, Bowral, Moss Vale, Berrima, Burradoo, Bundanoon, Robertson, Goulburn, Picton, Tahmoor, Bargo and Thirlmere. Explore our
            {" "}
            <Link to="/services" className="underline font-semibold hover:text-primary">full range of services</Link>
            , browse
            {" "}
            <Link to="/brands" className="underline font-semibold hover:text-primary">other brands we install</Link>
            , or
            {" "}
            <Link to="/contact-us" className="underline font-semibold hover:text-primary">contact us</Link>
            {" "}for a free, no-obligation onsite quote.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/contact-us" className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-semibold">
              Get a Free Quote
            </Link>
            <a href="tel:0480841485" className="px-6 py-3 border border-accent-foreground/30 text-accent-foreground rounded-lg hover:bg-accent-foreground/10 transition-colors text-sm font-semibold">
              Call 0480 841 485
            </a>
          </div>
        </div>
      </section>

      <CTAFooter />
    </div>
  );
};

export default BrandDetail;
